import { useIsFetching, useQueryClient } from '@tanstack/react-query'
import { AlertCircle, Info, RefreshCw } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'

import { useState } from 'react'

import { useStoreAnalytics } from '@/hooks/features/analytics'

import { Button } from '@/components/ui'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

import { PageHeader } from '@/components/atoms'
import { ReportToolbar } from '@/components/reports/ReportToolbar'
import { useReportRange } from '@/components/reports/useReportRange'

import { FunnelTab } from './tabs/FunnelTab'
import { HealthTab } from './tabs/HealthTab'
import { OverviewTab } from './tabs/OverviewTab'
import { ProductsTab } from './tabs/ProductsTab'
import { TrafficTab } from './tabs/TrafficTab'

import type { AnalyticsErrors, AnalyticsOverview } from '@/types/analytics'

const DEVICES = [
  { value: 'all', label: 'All devices' },
  { value: 'mobile', label: 'Mobile' },
  { value: 'desktop', label: 'Desktop' },
  { value: 'tablet', label: 'Tablet' },
]

const TABS = [
  { value: 'overview', label: 'Overview' },
  { value: 'funnel', label: 'Sales funnel' },
  { value: 'products', label: 'Products' },
  { value: 'traffic', label: 'Traffic' },
  { value: 'health', label: 'Store health' },
] as const

type Tab = (typeof TABS)[number]['value']

const pill = 'h-7 w-auto gap-1.5 rounded-full border-0 bg-fill px-3 text-xs font-medium'

/**
 * Storefront analytics from the store's own visit data, in five tabs that each answer one question: how the store is
 * doing, where shoppers give up, which products work, where visitors come from, and whether the store is fast and
 * working. The tab comes from `?tab=`; the dates and device apply to every tab, and each tab loads only its own data.
 * Orders here are online orders placed (cancelled left out); the Sales report counts delivered sales instead.
 * Rates from fewer than 100 visits are marked, and design comparisons are described as "after", never "because of".
 */
export const Analytics = () => {
  const [params, setParams] = useSearchParams()
  const tab: Tab = TABS.find((t) => t.value === params.get('tab'))?.value ?? 'overview'
  const selectTab = (value: string) => setParams(value === 'overview' ? {} : { tab: value })

  const { preset, range, onPresetChange, onRangeChange } = useReportRange()
  const [device, setDevice] = useState('all')

  const queryClient = useQueryClient()
  const reloading = useIsFetching({ queryKey: ['analytics'] }) > 0
  const reload = () => queryClient.invalidateQueries({ queryKey: ['analytics'] })

  const filters = { ...range, ...(device !== 'all' ? { device } : {}) }
  const overview = useStoreAnalytics<AnalyticsOverview>('overview', filters)
  // Loaded for every tab so the Store health tab can show how many problems shoppers hit.
  const errors = useStoreAnalytics<AnalyticsErrors>('errors', filters)

  const o = overview.data
  const noVisits = o && o.totals.sessions === 0
  const problems = errors.data ? errors.data.scripts.length + errors.data.api.length + errors.data.orders.length : 0

  return (
    <div className="space-y-4">
      <PageHeader title="Analytics" description="How shoppers find and move through your store, from your storefront's own visit data." />
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <ReportToolbar preset={preset} range={range} onPresetChange={onPresetChange} onRangeChange={onRangeChange} />
        <Label htmlFor="analytics-device" className="sr-only">
          Device
        </Label>
        <Select value={device} onValueChange={setDevice}>
          <SelectTrigger id="analytics-device" className={pill}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DEVICES.map((d) => (
              <SelectItem key={d.value} value={d.value}>
                {d.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="icon" className="sm:ml-auto" onClick={reload} disabled={reloading} aria-label="Reload analytics">
          <RefreshCw className={reloading ? 'animate-spin' : undefined} />
        </Button>
      </div>

      {overview.isError && (
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertTitle>Could not load analytics</AlertTitle>
          <AlertDescription>{overview.error.message}</AlertDescription>
        </Alert>
      )}

      {noVisits && (
        <Alert>
          <Info />
          <AlertTitle>No visits recorded in these dates</AlertTitle>
          <AlertDescription>
            Visits are recorded from the moment your storefront runs the analytics update. New visits show here within about 15 minutes.
          </AlertDescription>
        </Alert>
      )}

      {o?.low_sample && !noVisits && (
        <Alert>
          <Info />
          <AlertTitle>Not many visits yet</AlertTitle>
          <AlertDescription>
            Fewer than {o.min_sample} sessions in these dates, so rates can swing a lot from day to day. Use a longer date range for a steadier picture.
          </AlertDescription>
        </Alert>
      )}

      <Tabs value={tab} onValueChange={selectTab} className="space-y-4">
        <div className="no-scrollbar overflow-x-auto">
          <TabsList>
            {TABS.map((t) => (
              <TabsTrigger key={t.value} value={t.value}>
                {t.label}
                {t.value === 'health' && problems > 0 ? ` · ${problems}` : ''}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        <TabsContent value="overview">
          <OverviewTab o={o} range={range} onSeeProducts={() => selectTab('products')} />
        </TabsContent>
        <TabsContent value="funnel">
          <FunnelTab o={o} range={range} device={device} />
        </TabsContent>
        <TabsContent value="products">
          <ProductsTab range={range} filters={filters} />
        </TabsContent>
        <TabsContent value="traffic">
          <TrafficTab filters={filters} />
        </TabsContent>
        <TabsContent value="health">
          <HealthTab range={range} filters={filters} errors={errors} />
        </TabsContent>
      </Tabs>
    </div>
  )
}
